import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inboxApi } from '@wa/shared';

/**
 * Paste numbers, get members — the reference's "Quick Import".
 *
 * It is the ordinary import with the spreadsheet step removed: the pasted text
 * becomes a two-column CSV and goes through the same job, the same validation and
 * the same per-row report as a file would. A second import path with its own
 * parsing would be a second place for a number to be read wrongly.
 *
 * One number per line, with an optional name before it separated by a comma or a
 * tab — which is what a column copied out of a spreadsheet pastes as.
 */
export function QuickImportDialog({ segmentId, onClose }: { segmentId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rows = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(/[,\t]/).map((p) => p.trim());
    const phoneIx = parts.findIndex((p) => /^\+?\d[\d\s-]{6,}$/.test(p));
    if (phoneIx < 0) return null;
    const phone = parts[phoneIx]!.replace(/[\s-]/g, '');
    const name = parts.filter((_, i) => i !== phoneIx).join(' ').trim();
    return { phone, name };
  });
  const valid = rows.filter((r): r is { phone: string; name: string } => r !== null);
  const bad = rows.length - valid.length;

  const run = useMutation({
    mutationFn: async () => {
      const csv = ['phone,name', ...valid.map((r) => `${r.phone},"${r.name.replace(/"/g, '""')}"`)].join('\n');
      const job = await inboxApi.createImport({
        fileName: `quick-import-${Date.now()}.csv`, content: csv, mapping: { phone: 0, name: 1 }, segmentId,
      });
      // The import runs in the background. Wait for it — a paste of forty numbers
      // finishes in a second, and a dialog that closes before the list changes
      // makes the person paste them again.
      for (let i = 0; i < 40; i += 1) {
        await new Promise((r) => setTimeout(r, 500));
        const { job: state } = await inboxApi.import(job.id);
        if (state.status === 3) throw new Error(state.lastError ?? 'The import failed.');
        if (state.status === 2) return state;
      }
      return null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segment-members', segmentId] });
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'The import could not be started.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Quick import" onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop)">
        <header className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="text-lead font-semibold">Quick Import</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="tap grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </header>
        <div className="px-4 pb-4">
          <p className="text-small text-(--color-muted)">One number per line, with the country code. A name before it, separated by a comma, is kept.</p>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder={'919876543210\nAsha Menon, 919876543211'}
            className="mt-2 w-full resize-y rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2 font-mono text-small outline-none focus:border-(--color-brand)"
          />
          <p className="mt-1.5 text-small text-(--color-muted)">
            {valid.length} {valid.length === 1 ? 'number' : 'numbers'}
            {bad > 0 && <span className="text-(--color-warn)"> · {bad} {bad === 1 ? 'line' : 'lines'} without a number will be skipped</span>}
          </p>
          {error && <p role="alert" className="mt-2 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
            <button
              type="button"
              disabled={valid.length === 0 || run.isPending}
              onClick={() => run.mutate()}
              className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60"
            >
              {run.isPending ? 'Importing…' : `Add ${valid.length || ''} to list`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
